import { CanvasEntity, DrawingUtils, FloatAnimation, ICanvasEntity, InputSystem, subscriptionListener } from '@adaptiva/canvas';
import { EOperatingSystem, IconService } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { Box, IVector2, Vector2 } from '@adaptiva/core/space';
import { AdaColor, IThemeModel } from '@adaptiva/core/themes';
import { UiService } from '@adaptiva/core/ui';
import { IMemoryPipelineNode } from '@adaptiva/data-service';
import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { startWith } from 'rxjs';
import { MemoryPipelineNodeEntity } from '../../entities';

@Injectable()
export class MemoryPipelineDrawingService extends CanvasEntity {

	public static readonly DEFAULT_ICON: string = 'faCube';

	public static readonly LINE_DASH_OFFSET: float = 30.0;

	public override layer: int = 2;

	protected lineDashAnimation: FloatAnimation;

	protected lineDashOffset: float = 0.0;

	protected images: Map<string, HTMLImageElement> = new Map();

	protected font: string = 'Museo Sans';

	public rootEntity: MemoryPipelineNodeEntity;

	public borderColor: AdaColor;

	public backgroundColor: AdaColor;

	public margin: int = 12;

	public verticalSpacing: int = 25;

	public horizontalSpacing: int = 25;

	public get verticalOffset(): int {
		return this.verticalSpacing + (this.margin * 2);
	}

	protected get lineDash(): Vector2 {
		return new Vector2(5, 10);
	}

	public get fontSize(): float {
		return this.ui.Spacing.SPACING_MEDIUM_NUMERIC * this.ui.BASE_UNIT_PX;
	}

	public get padding(): float {
		return this.ui.Spacing.SPACING_MEDIUM_NUMERIC * this.ui.BASE_UNIT_PX;
	}

	public ui: UiService = inject(UiService);

	protected loggingService: LoggingService = inject(LoggingService);

	protected iconService: IconService = inject(IconService);

	protected cursor: InputSystem.Cursor = inject(InputSystem.Cursor);

	public override start(): void {
		super.start();
		this.determineColors();
	}

	public override draw(_context: CanvasRenderingContext2D): void {
		if (this.rootEntity) {
			this.drawConnections(_context, this.rootEntity);
			this.drawPercentages(_context, this.rootEntity);
		}
	}

	public determineColors(): void {
		this.destructibles.push(
			subscriptionListener(
				toObservable(this.engine.themeService.theme, { injector: this.injector }).pipe(
					startWith(this.engine.themeService.theme())
				).subscribe({
					next: (_theme: IThemeModel) => {
						this.borderColor = AdaColor.fromRgb(_theme.backgroundColor).darken(20);
						this.backgroundColor = AdaColor.fromRgb(_theme.backgroundColor);

						this.resetIcons();
					}
				})
			)
		);
	}

	protected resetIcons(): void {
		this.images.forEach((i) => i.remove());
		this.images.clear();
	}

	private _checkIcon(_iconName: string): IconDefinition {
		_iconName = _iconName || MemoryPipelineDrawingService.DEFAULT_ICON;

		if (!this.iconService.icons.allIcons.has(_iconName)) {
			this.loggingService.warn(
				'MemoryPipelineDrawingService.getIcon',
				'No icon for ' + _iconName + '. Falling back to ' + MemoryPipelineDrawingService.DEFAULT_ICON + '.'
			)();
			_iconName = MemoryPipelineDrawingService.DEFAULT_ICON;
		}

		return this.iconService.icons.allIcons.get(_iconName) || null;
	}

	public getIcon(_iconName: string, _color: AdaColor): HTMLImageElement {
		const _iconKey: string = _iconName + _color.hex();
		const _icon: IconDefinition = this._checkIcon(_iconName);

		if (this.images.has(_iconKey))
			return this.images.get(_iconKey);

		const _svgContent: string = _icon.icon[4] as string;
		const _svgFillColor: string = _color.hex();
		const _svgData: string = `<svg width="${this.fontSize}" height="${this.fontSize}" viewBox="0 0 ${_icon.icon[0]} ${_icon.icon[1]}">` +
			`<path fill="${_svgFillColor}" d="${_svgContent}"></path>` +
			'</svg>';
		const _wrapper: HTMLElement = document.createElement('div');

		_wrapper.innerHTML = _svgData;

		const _svgElement: ChildNode = _wrapper.firstChild;
		const _serializedSvg: string = new XMLSerializer().serializeToString(_svgElement);
		const _svgBase64: string = 'data:image/svg+xml;base64,' + btoa(_serializedSvg);
		const _image: HTMLImageElement = new Image();

		_image.onload = () => {
			delete _image.onload;
			_wrapper.remove();
		};

		_image.src = _svgBase64;
		_image.width = _icon.icon[0];
		_image.height = _icon.icon[1];

		this.images.set(_iconKey, _image);

		return _image;
	}

	public drawIcon(_context: CanvasRenderingContext2D, _iconName: string, _entity: ICanvasEntity, _color: AdaColor, _dimensions?: Vector2): void {
		const _icon: HTMLImageElement = this.getIcon(_iconName, _color);

		if (_icon.complete) {
			const _dx: float = (_entity.position.x - (_entity.width / 2)) + this.padding;
			const _dy: float = _entity.position.y + this.padding;

			_context.drawImage(_icon, _dx, _dy, _dimensions?.x, _dimensions?.y);
		}
	}

	public drawNode(_context: CanvasRenderingContext2D, _entity: MemoryPipelineNodeEntity): void {
		this.drawNodeBackground(_context, _entity);
		this.drawNodeText(_context, _entity, _entity.iconDimensions);
		this.drawIcon(
			_context,
			EOperatingSystem.toIcon(_entity.node.operatingSystem),
			_entity,
			_entity.isSelected ? _entity.highlightColor : _entity.borderColor,
			_entity.iconDimensions
		);
	}

	public drawNodeBackground(_context: CanvasRenderingContext2D, _entity: MemoryPipelineNodeEntity): void {
		DrawingUtils.drawRectangle({
			context: _context,
			bounds: new Box({
				topLeft: Vector2.from(_entity.position).subtract({
					x: _entity.width / 2
				}),
				bottomRight: new Vector2(
					_entity.position.x + (_entity.width / 2),
					_entity.position.y + _entity.height
				)
			}),
			fillColor: _entity.isSelected ? _entity.borderColor.hex() : _entity.backgroundColor.hex(),
			borderColor: _entity.borderColor.hex(),
			borderRadius: [3, 3, 3, 3],
			lineWidth: 2.0
		});
	}

	public drawConnections(_context: CanvasRenderingContext2D, _entity: MemoryPipelineNodeEntity): void {
		_entity.node.clients.forEach((_child: IMemoryPipelineNode) => {
			const _childEntity: MemoryPipelineNodeEntity = (this.engine.getEntity(_child.hostName) as any) as MemoryPipelineNodeEntity;

			if (!_entity.isHiding)
				this.connectPath(_context, _childEntity, _entity);

			this.drawConnections(_context, _childEntity);
		});
	}

	public drawPercentages(_context: CanvasRenderingContext2D, _entity: MemoryPipelineNodeEntity): void {
		if (_entity.percentagePosition) {
			const _percentText: string = `${_entity.node.percentComplete}%`;
			const _textMeasurement: TextMetrics = _context.measureText(_percentText);
			const _textPosition: Vector2 = new Vector2(
				_entity.percentagePosition.x - (_textMeasurement.width / 2),
				_entity.percentagePosition.y - (this.fontSize / 2)
			);

			this.drawRectangle(
				_context,
				_textPosition.copy().subtract({ x: 4, y: 4 }),
				new Vector2(_textMeasurement.width + 8, this.fontSize + 8)
			);
			this.drawText(_context, _percentText, _textPosition, _entity.textColor);
		}

		_entity.node.clients.forEach((_child: IMemoryPipelineNode) => {
			const _childEntity: MemoryPipelineNodeEntity = (this.engine.getEntity(_child.hostName) as any) as MemoryPipelineNodeEntity;

			this.drawPercentages(_context, _childEntity);
		});
	}

	public connectPath(_context: CanvasRenderingContext2D, _to: MemoryPipelineNodeEntity, _from: MemoryPipelineNodeEntity): void {
		if (!_from || !_to)
			return;

		let _origin: Vector2;
		let _destination: Vector2;
		let _shouldDrawArrow: boolean = false;
		let _lineDash: Vector2 = Vector2.zero();

		if (_from === _to.parent) {
			_origin = new Vector2(
				_from.position.x,
				_from.position.y + _from.height
			);
			_destination = Vector2.from(_to.position);
			_shouldDrawArrow = true;
		} else
			return;

		_context.lineWidth = 2;

		let _bendPoint: float;
		const _path: Path2D = new Path2D();
		const _distance: Vector2 = _destination.copy().subtract(_origin);
		const _negativeOffset: float = 12; // this.layoutService.margin;
		let _color: string = AdaColor.fromRgb(this.engine.themeService.theme().primaryColor).hex();

		if (_distance.y < 0)
			_bendPoint = _origin.y + _negativeOffset;
		else {
			_bendPoint = Math.max(
				_origin.y + (_distance.y / 2),
				_destination.y - 25 // this.layoutService.verticalSpacing
			);
		}

		if (_to.node.percentComplete < 100) {
			if (!this.lineDashAnimation)
				this.createLineDashAnimation();

			_lineDash = this.lineDash;
		} else
			_color = AdaColor.fromRgb([46, 204, 113, 1]).hex();

		_path.moveTo(_origin.x, _origin.y);
		_path.lineTo(_origin.x, _bendPoint);
		_path.lineTo(_destination.x, _bendPoint);
		_path.lineTo(_destination.x, _destination.y);

		_context.setLineDash(_lineDash.toArray());
		_context.strokeStyle = _color;
		_context.lineDashOffset = (this.lineDashAnimation && this.lineDashOffset) || 0.0;
		_context.stroke(_path);

		_path.closePath();
		_context.setLineDash([]);
		_context.lineDashOffset = 0;

		_to.percentagePosition = new Vector2(_destination.x, _bendPoint);

		if (_shouldDrawArrow) {
			const _arrowPosition: Vector2 = _distance.y < 0 ? _destination.copy().add({
				y: _to.height
			}) : _destination;

			this.drawArrow(_context, _arrowPosition, _color);
		}
	}

	public drawNodeText(_context: CanvasRenderingContext2D, _node: MemoryPipelineNodeEntity, _offset?: Vector2): void {
		const _dx: float = _node.position.x - (_node.width / 2) + (this.padding + _offset.x + (this.padding / 2));
		const _dy: float = _node.position.y + ((_node.height - _node.fontSize) / 2);
		const _fillColor: AdaColor = _node.isSelected ? _node.highlightColor : _node.textColor;

		this.drawText(_context, _node.node.displayText, new Vector2(_dx, _dy), _fillColor);
	}

	public drawText(_context: CanvasRenderingContext2D, _text: string, _position: Vector2, _color: AdaColor): void {
		_context.font = `${this.fontSize}px Museo Sans`;
		_context.textBaseline = 'top';
		_context.fillStyle = _color.hex();

		_context.fillText(_text, _position.x, _position.y);
	}

	public drawRectangle(_context: CanvasRenderingContext2D, _position: Vector2, _dimensions: Vector2): void {
		DrawingUtils.drawRectangle({
			context: _context,
			bounds: new Box({
				topLeft: Vector2.from(_position),
				bottomRight: new Vector2(
					_position.x + _dimensions.x,
					_position.y + _dimensions.y
				)
			}),
			fillColor: this.backgroundColor.hex(),
			borderColor: this.borderColor.hex(),
			borderRadius: [3, 3, 3, 3],
			lineWidth: 2.0
		});
	}

	protected drawArrow(
		_context: CanvasRenderingContext2D,
		_position: Vector2,
		_color: string
	): void {
		const _path: Path2D = new Path2D();
		const _sideOffset: float = 4.0;
		const _topOffset: float = 6.0;
		const _topCorner: float = _position.y - _topOffset;

		const _firstSide: Partial<IVector2> = {
			x: _position.x - _sideOffset,
			y: _topCorner
		};
		const _secondSide: Partial<IVector2> = {
			x: _position.x + _sideOffset,
			y: _topCorner
		};

		_path.moveTo(_position.x, _position.y);
		_path.lineTo(_firstSide.x, _firstSide.y);
		_path.lineTo(_secondSide.x, _secondSide.y);
		_path.lineTo(_position.x, _position.y);
		_path.closePath();

		_context.fillStyle = _color;
		_context.fill(_path);
	}

	protected createLineDashAnimation(): void {
		this.lineDashAnimation = FloatAnimation.create<FloatAnimation>(this.injector, {
			target: this,
			duration: 1,
			autoDestroy: true,
			loop: true,
			keyframes: [
				{
					property: 'lineDashOffset',
					time: 0.0,
					value: 0.0,
				},
				{
					property: 'lineDashOffset',
					time: 1.0,
					value: -MemoryPipelineDrawingService.LINE_DASH_OFFSET,
				}
			]
		});

		this.attach(this.lineDashAnimation);
	}

}

export namespace MemoryPipelineDrawingService {

	export type EventTypes = CanvasEntity.EventTypes | 'CONNECTION_MOUSEOVER';

	export const events: Record<EventTypes, string> = {
		...CanvasEntity.events,
		CONNECTION_MOUSEOVER: 'connectionmouseover',
	};

}
